import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import {
  UserGroupIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ChartBarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import dashboardService, { DashboardStats, ChartData } from '../services/dashboard.service';
import PagosMensualesChart from '../components/Charts/PagosMensualesChart';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);


const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [contratosPorMes, setContratosPorMes] = useState<ChartData | null>(null);
  const [estadoContratos, setEstadoContratos] = useState<ChartData | null>(null);
  const [conceptosContratados, setConceptosContratados] = useState<any[]>([]);
  const [expiringCount, setExpiringCount] = useState<number>(0);
  const [expiringPaymentsCount, setExpiringPaymentsCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const years = [2025, 2026, 2027, 2028];

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [summaryData, contratosData, estadoData, conceptosData, expiringData, expiringPaymentsData] = await Promise.all([
          dashboardService.getSummary(selectedYear),
          dashboardService.getContratosPorMes(selectedYear),
          dashboardService.getEstadoContratos(selectedYear),
          dashboardService.getConceptosContratados(selectedYear),
          dashboardService.getExpiringContractsCount(),
          dashboardService.getExpiringPaymentsCount()
        ]);
        
        setStats(summaryData);
        setContratosPorMes(contratosData);
        setEstadoContratos(estadoData);
        setConceptosContratados(conceptosData);
        setExpiringCount(expiringData.count);
        setExpiringPaymentsCount(expiringPaymentsData.count);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedYear]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Clientes Activos',
      value: stats?.clientes.activos || 0,
      icon: UserGroupIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Contratos Vigentes',
      value: stats?.contratos.total || 0,
      icon: DocumentTextIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Pagos Realizados',
      value: stats?.pagos.total || 0,
      icon: CreditCardIcon,
      color: 'bg-yellow-500',
    },
    {
      name: 'Propuestas Activas',
      value: stats?.propuestas.total || 0,
      icon: ChartBarIcon,
      color: 'bg-purple-500',
    },
  ];

  const chartData = {
    labels: contratosPorMes?.labels || ['Sin datos'],
    datasets: [
      {
        label: 'Contratos Firmados',
        data: contratosPorMes?.data || [0],
        backgroundColor: 'rgba(47, 85, 151, 0.8)',
        borderColor: 'rgba(47, 85, 151, 1)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Contratos por Mes',
      },
    },
  };

  const pieData = {
    labels: estadoContratos?.labels || ['Vigentes', 'No Vigentes'],
    datasets: [
      {
        data: estadoContratos?.data || [0, 0],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)', // Green for Vigentes
          'rgba(239, 68, 68, 0.8)', // Red for No Vigentes
        ],
        borderColor: [
          'rgba(34, 197, 94, 1)',
          'rgba(239, 68, 68, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div>
      {/* Header & Year Selector */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-sm text-gray-700">
            Resumen general del sistema CRM
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-4">
          <div className="flex items-center bg-white rounded-md shadow-sm border border-gray-300 px-3 py-2">
            <span className="text-sm text-gray-500 mr-2">Año:</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="block w-full pl-3 pr-10 py-1 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Acciones Rápidas (Moved to Top) */}
      <div className="bg-white overflow-hidden shadow rounded-lg p-6 mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Acciones Rápidas
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/clientes/nuevo"
            className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium py-3 px-4 rounded-md transition-colors duration-200 block text-center shadow-sm"
          >
            Nuevo Cliente
          </Link>
          <Link
            to="/contratos/nuevo"
            className="bg-green-500 hover:bg-green-600 text-white text-sm font-medium py-3 px-4 rounded-md transition-colors duration-200 block text-center shadow-sm"
          >
            Nuevo Contrato
          </Link>
          <Link
            to="/pagos/nuevo"
            className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-3 px-4 rounded-md transition-colors duration-200 block text-center shadow-sm"
          >
            Registrar Pago
          </Link>
          <Link
            to="/reportes"
            className="bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium py-3 px-4 rounded-md transition-colors duration-200 block text-center shadow-sm"
          >
            Generar Reporte
          </Link>
        </div>
      </div>

      {/* Alerts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Expiring Contracts Widget */}
        <div 
          onClick={() => navigate('/contratos?expiring=true')}
          className="bg-red-50 border-l-4 border-red-500 p-4 cursor-pointer hover:bg-red-100 transition-colors shadow-sm rounded-r-md"
        >
          <div className="flex">
            <div className="flex-shrink-0">
              <ClockIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Contratos que se vencen este mes
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  Hay <span className="font-bold text-lg">{expiringCount}</span> contratos próximos a vencer.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Expiring Payments Widget */}
        <div 
          onClick={() => navigate('/pagos?alert=true')}
          className="bg-blue-50 border-l-4 border-blue-500 p-4 cursor-pointer hover:bg-blue-100 transition-colors shadow-sm rounded-r-md"
        >
          <div className="flex">
            <div className="flex-shrink-0">
              <CreditCardIcon className="h-5 w-5 text-blue-500" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Ingresos próximos a vencer
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Hay <span className="font-bold text-lg">{expiringPaymentsCount}</span> pagos pendientes o por vencer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="bg-white overflow-hidden shadow rounded-lg"
            >
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`${stat.color} p-3 rounded-md`}>
                      <Icon className="h-6 w-6 text-white" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stat.value.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Resumen Financiero
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Ingresos del Mes</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${stats?.pagos.ingresosMensuales?.toLocaleString() || '0'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Ingresos Anuales</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${stats?.pagos.ingresosAnuales?.toLocaleString() || '0'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Pagos Pendientes</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats?.pagos.pagosPendientes?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        {/* Gráfica de Pagos Mensuales */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Pagos Mensuales
            </h3>
            <PagosMensualesChart />
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg p-6">
          <Bar data={chartData} options={chartOptions} />
        </div>

        {/* Active/Inactive Contracts Chart */}
        <div className="bg-white overflow-hidden shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Estado de Contratos
          </h3>
          <div className="h-64 flex justify-center">
             <Pie data={pieData} />
          </div>
        </div>
        
        <div className="bg-white overflow-hidden shadow rounded-lg p-6 lg:col-span-2">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Conceptos Contratados - {selectedYear}
          </h3>
          <div className="overflow-y-auto max-h-96">
            {conceptosContratados.length > 0 ? (
              <div className="space-y-2">
                {conceptosContratados.map((concepto) => (
                  <div key={concepto.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {concepto.codigo && (
                          <span className="text-xs font-mono text-gray-500 bg-white px-2 py-1 rounded">
                            {concepto.codigo}
                          </span>
                        )}
                        <p className="text-sm font-medium text-gray-900">{concepto.nombre}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Cantidad: {concepto.cantidadContratada.toLocaleString('es-CO')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-primary-600">
                        {formatCurrency(concepto.valorTotal)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No hay conceptos contratados este año
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;